import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder, 
  className = "",
  name 
}) => {
  const { t } = useLanguage();
  const editorRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [storedRange, setStoredRange] = useState(null);

  // Handle right-click to show custom formatting toolbar (replace browser context menu)
  const handleContextMenu = (e) => {
    e.preventDefault(); // Prevent browser's native context menu
    
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    if (selectedText.length > 0) {
      // Store the current selection range
      if (selection.rangeCount > 0) {
        setStoredRange(selection.getRangeAt(0).cloneRange());
      }
      
      setShowDropdown(true);
      
      // Get the editor's position relative to the viewport
      const editorRect = editorRef.current.getBoundingClientRect();
      
      // Position dropdown right next to the click position, but within the editor bounds
      const clickX = e.clientX - editorRect.left;
      const clickY = e.clientY - editorRect.top;
      
      // Adjust position to stay within editor bounds
      let left = clickX;
      let top = clickY + 20; // Small offset below cursor
      
      // Ensure dropdown doesn't go outside editor bounds
      if (left > editorRect.width - 200) {
        left = editorRect.width - 200; // 200px is approximate dropdown width
      }
      if (top > editorRect.height - 300) {
        top = clickY - 300; // Show above if not enough space below
      }
      
      setDropdownPosition({
        top: top,
        left: left
      });
    } else {
      setShowDropdown(false);
    }
  };

  // Format text functions
  const formatText = (command, value = null) => {
    if (!storedRange) return;
    
    // Focus the editor first
    editorRef.current.focus();
    
    // Restore the selection
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(storedRange);
    
    // Handle custom commands
    if (command === 'uppercase' || command === 'lowercase') {
      const selectedText = selection.toString();
      const transformedText = command === 'uppercase' 
        ? selectedText.toUpperCase() 
        : selectedText.toLowerCase();
      
      // Replace selected text with transformed text
      try {
        document.execCommand('insertText', false, transformedText);
      } catch (e) {
        console.error('Text transform failed:', e);
      }
    } else {
      // Apply formatting using execCommand (it's still the most reliable for contentEditable)
      try {
        document.execCommand(command, false, value);
      } catch (e) {
        console.error('Format command failed:', e);
      }
    }
    
    // Close dropdown and clear stored range
    setShowDropdown(false);
    setStoredRange(null);
    
    // Trigger onChange
    setTimeout(() => {
      const newValue = editorRef.current.innerHTML;
      onChange({ target: { name, value: newValue } });
    }, 0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editorRef.current && !editorRef.current.contains(event.target)) {
        setShowDropdown(false);
        setStoredRange(null);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle paste with formatting preservation but remove background highlights
  const handlePaste = (e) => {
    e.preventDefault();
    
    const clipboardData = e.clipboardData || window.clipboardData;
    const htmlData = clipboardData.getData('text/html');
    const textData = clipboardData.getData('text/plain');
    
    if (htmlData) {
      // Create a temporary div to parse and clean HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlData;
      
      // Function to recursively process nodes and remove block elements
      const processNode = (node) => {
        // If it's a block-level element, replace with its inline content
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          const blockTags = ['div', 'p', 'section', 'article', 'header', 'footer', 'main', 'aside', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li'];
          
          if (blockTags.includes(tagName)) {
            // Create a document fragment to hold the children
            const fragment = document.createDocumentFragment();
            
            // Move all children to the fragment
            while (node.firstChild) {
              fragment.appendChild(node.firstChild);
            }
            
            // Replace the block element with its children
            node.parentNode.replaceChild(fragment, node);
            return;
          }
          
          // Remove <br> tags to prevent extra line breaks
          if (tagName === 'br') {
            node.remove();
            return;
          }
          
          // For inline elements (span, strong, em, etc.), keep them but remove problematic styles
          if (node.hasAttribute('style')) {
            let style = node.getAttribute('style');
            // Remove background styles
            style = style.replace(/background[^;]*;?/gi, '');
            style = style.replace(/background-color[^;]*;?/gi, '');
            if (style.trim()) {
              node.setAttribute('style', style);
            } else {
              node.removeAttribute('style');
            }
          }
        }
        
        // Process children
        const children = Array.from(node.childNodes);
        children.forEach(child => processNode(child));
      };
      
      // Process all nodes in tempDiv
      Array.from(tempDiv.childNodes).forEach(child => processNode(child));
      
      // Get cleaned HTML
      let cleanHtml = tempDiv.innerHTML;
      
      // Final cleanup: remove extra whitespace and newlines
      cleanHtml = cleanHtml
        .replace(/\n+/g, ' ') // Replace newlines with space
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
      
      // If we have content, paste it
      if (cleanHtml) {
        document.execCommand('insertHTML', false, cleanHtml);
      }
    } else {
      // For plain text, only convert newlines if they exist
      const cleanText = textData.trim();
      if (cleanText) {
        // Only convert newlines to br tags if the text contains newlines
        if (cleanText.includes('\n')) {
          const textWithBreaks = cleanText.replace(/\n/g, '<br>');
          document.execCommand('insertHTML', false, textWithBreaks);
        } else {
          // Single line text - paste as-is without any breaks
          document.execCommand('insertText', false, cleanText);
        }
      }
    }
    
    // Trigger change event
    const newValue = editorRef.current.innerHTML;
    onChange({ target: { name, value: newValue } });
  };

  // Handle input changes
  const handleInput = () => {
    const newValue = editorRef.current.innerHTML;
    onChange({ target: { name, value: newValue } });
  };


  // Color picker options
  const colors = [
    { name: 'Default', value: '#ffffff' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Yellow', value: '#eab308' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Gray', value: '#6b7280' }
  ];

  // Set initial content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDropdown && !e.target.closest('.format-dropdown')) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div className="relative">
      {/* Rich Text Editor */}
      <div
        ref={editorRef}
        contentEditable
        className={`w-full min-h-32 bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none resize-none ${className}`}
        style={{ minHeight: '128px' }}
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        onContextMenu={handleContextMenu}
        suppressContentEditableWarning={true}
      />

      {/* Custom Formatting Toolbar (replaces browser context menu) */}
      {showDropdown && (
        <div
          className="format-dropdown absolute z-50 bg-gray-700 border border-gray-600 rounded-lg shadow-lg py-2 min-w-48"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            position: 'absolute'
          }}
        >
          {/* Bold Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              formatText('bold');
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white flex items-center gap-3"
          >
            <strong className="text-lg">B</strong>
            <span>Bold</span>
          </button>

          {/* Italic Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              formatText('italic');
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white flex items-center gap-3"
          >
            <em className="text-lg">I</em>
            <span>Italic</span>
          </button>

          {/* Underline Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              formatText('underline');
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white flex items-center gap-3"
          >
            <u className="text-lg">U</u>
            <span>Underline</span>
          </button>

          {/* Separator */}
          <div className="border-t border-gray-600 my-1"></div>

          {/* Uppercase Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              formatText('uppercase');
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white flex items-center gap-3"
          >
            <span className="text-lg font-bold">AA</span>
            <span>Uppercase</span>
          </button>

          {/* Lowercase Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              formatText('lowercase');
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white flex items-center gap-3"
          >
            <span className="text-lg">aa</span>
            <span>Lowercase</span>
          </button>

          {/* Separator */}
          <div className="border-t border-gray-600 my-1"></div>

          {/* Font Size Options */}
          <div className="px-4 py-2">
            <div className="text-xs text-gray-300 mb-2">Font Size:</div>
            <div className="grid grid-cols-4 gap-1">
              {[
                { size: '1', label: 'XS' },
                { size: '3', label: 'S' },
                { size: '4', label: 'M' },
                { size: '5', label: 'L' },
                { size: '6', label: 'XL' },
                { size: '7', label: 'XXL' }
              ].map((item) => (
                <button
                  key={item.size}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    formatText('fontSize', item.size);
                  }}
                  className="px-2 py-1 text-center hover:bg-gray-600 text-white text-xs border border-gray-500 rounded transition-colors"
                  title={`Size ${item.label}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-gray-600 my-1"></div>

          {/* Color Options */}
          <div className="px-4 py-2">
            <div className="text-xs text-gray-300 mb-2">Colors:</div>
            <div className="grid grid-cols-5 gap-1">
              {colors.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    formatText('foreColor', color.value);
                  }}
                  className="w-8 h-8 rounded border border-gray-500 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Custom Color Option */}
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              const color = prompt('Enter color (e.g., #ff0000 or red):');
              if (color) {
                formatText('foreColor', color);
              } else {
                setShowDropdown(false);
              }
            }}
            className="w-full px-4 py-2 text-left hover:bg-gray-600 text-white text-sm"
          >
            Custom Color...
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-2 text-xs text-gray-400">
        <p>💡 <strong>{t('tips')}</strong></p>
        <p>• {t('tipSelectText')}</p>
        <p>• {t('tipChooseFormatting')}</p>
        <p>• {t('tipCopyFormatted')}</p>
      </div>
    </div>
  );
};

export default RichTextEditor;
